import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeGridView } from "./reportGridModel";

const headers = { "Content-Type": "application/json", "X-Report-Preferences": "1" };

export default function useSharedReportView(reportKey, columns, defaultView = {}) {
  // Value identities keep render-created column arrays from restarting requests.
  // Each report retains its own revision and pending writes when routes change.
  const schemaIdentity = JSON.stringify(columns.map(column => ({ key: column.key, allowGroup: column.allowGroup })));
  const defaultsIdentity = JSON.stringify(defaultView);
  const store = useMemo(() => {
    const key = reportKey === "item-summary" ? "sales-customer-items" : reportKey;
    const schema = JSON.parse(schemaIdentity), defaults = JSON.parse(defaultsIdentity);
    const normalize = value => {
      if (key === "sales-customer-items" && value?.version === 1) {
        value = { version: 2, groupBy: value.grouped ? ["STK_GRP1_DESC"] : [], expanded: value.grouped && Array.isArray(value.expanded) ? value.expanded.filter(item => typeof item === "string").map(item => JSON.stringify([["STK_GRP1_DESC", item]])) : [], columnWidths: {} };
      }
      return normalizeGridView(value, schema, defaults);
    };
    let storage = null, username = "", cached = null;
    try {
      storage = window.localStorage;
      const user = JSON.parse(storage.getItem("currentUser") || "null");
      username = typeof user?.username === "string" ? user.username.trim() : "";
    } catch { /* Browser storage is an optional cache. */ }
    const storageKey = `agdxb:report-view:v2:${key}:${encodeURIComponent(username || "guest")}`;
    try {
      let raw = storage?.getItem(storageKey);
      if (!raw && key === "sales-customer-items") raw = storage?.getItem(`agdxb:item-summary:view:v1:${encodeURIComponent(username || "guest")}`);
      cached = JSON.parse(raw || "null");
    } catch { /* Invalid caches do not affect report data. */ }
    return { endpoint: `/api/report-preferences/${encodeURIComponent(key)}`, storage, storageKey, username, normalize,
      view: normalize(cached), revision: 0, authenticated: false, pending: null, saving: false, reading: false, mounted: false, status: "loading", error: "" };
  }, [reportKey, schemaIdentity, defaultsIdentity]);
  const [snapshot, setSnapshot] = useState(() => ({ owner: store, view: store.view, status: store.status, error: store.error }));
  const publish = useCallback(() => {
    if (store.mounted) setSnapshot({ owner: store, view: store.view, status: store.status, error: store.error });
  }, [store]);
  const showView = useCallback(next => {
    store.view = store.normalize(next);
    try { store.storage?.setItem(store.storageKey, JSON.stringify(store.view)); } catch { /* A full browser cache does not prevent server saves. */ }
    publish();
  }, [store, publish]);
  const setStatus = useCallback((status, error = "") => { store.status = status; store.error = error; publish(); }, [store, publish]);

  const flush = useCallback(async () => {
    if (store.saving || store.reading || !store.authenticated) return;
    store.saving = true;
    try {
      while (store.pending) {
        const next = store.pending;
        store.pending = null;
        setStatus("saving");
        try {
          const body = JSON.stringify({ view: next, revision: store.revision });
          const response = await fetch(store.endpoint, { method: "PUT", credentials: "same-origin", headers, body, keepalive: new TextEncoder().encode(body).length < 60000 });
          const payload = await response.json();
          if (response.status === 409 && payload.view) {
            store.pending = null;
            store.revision = payload.revision;
            showView(payload.view);
            setStatus("conflict", "Changed on another device. Latest saved view loaded.");
            return;
          }
          if (!response.ok) {
            store.pending = store.pending || next;
            if (response.status === 401) { store.authenticated = false; setStatus("signin"); }
            else setStatus("error", "View is not synced. Please retry.");
            return;
          }
          store.revision = payload.revision;
        } catch {
          store.pending = store.pending || next;
          setStatus("error", "View is not synced. Check the connection and retry.");
          return;
        }
      }
      setStatus("saved");
    } finally { store.saving = false; }
  }, [store, showView, setStatus]);

  const refresh = useCallback(async (preservePending = false) => {
    if (store.reading || store.saving || (store.pending && !preservePending)) return;
    store.reading = true;
    try {
      const response = await fetch(store.endpoint, { credentials: "same-origin", cache: "no-store", headers: { "X-Report-Preferences": "1" } });
      const payload = await response.json();
      if (response.status === 401 || (response.ok && store.username && payload.username?.toLowerCase() !== store.username.toLowerCase())) {
        store.authenticated = false;
        setStatus("signin");
        return;
      }
      if (!response.ok || !payload.view) throw new Error("Cannot load view");
      store.authenticated = true;
      store.revision = payload.revision;
      if (!store.pending) showView(payload.view);
      setStatus("saved");
    } catch { setStatus("error", "Shared settings are unavailable. Your changes are not synced."); }
    finally { store.reading = false; }
    if (store.pending && store.authenticated) await flush();
  }, [store, flush, showView, setStatus]);

  const setView = useCallback(updater => {
    const next = store.normalize(typeof updater === "function" ? updater(store.view) : updater);
    showView(next);
    store.pending = next;
    if (store.authenticated) void flush();
  }, [store, flush, showView]);

  useEffect(() => {
    store.mounted = true;
    publish();
    void refresh();
    const onFocus = () => { if (!document.hidden) void refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const timer = window.setInterval(onFocus, 30000);
    return () => {
      store.mounted = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [store, publish, refresh]);

  const signIn = useCallback(async (username, password) => {
    const response = await fetch("/api/report-preferences/session", { method: "POST", credentials: "same-origin", headers, body: JSON.stringify({ username, password }) });
    if (!response.ok) throw new Error(response.status === 429 ? "Too many attempts. Please try again later." : "Could not verify this account. Use your portal account credentials.");
    await refresh(true);
  }, [refresh]);
  const retry = useCallback(async () => {
    if (store.authenticated && store.pending) await flush();
    else await refresh(true);
  }, [store, flush, refresh]);
  const current = snapshot.owner === store ? snapshot : store;
  return { view: current.view, setView, status: current.status, error: current.error, username: store.username, signIn, retry };
}
